import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  PackageOpen, FileText, ArrowLeftRight, Scale, BookOpen, Truck, Warehouse, Globe2,
} from "lucide-react";
import Modal, { Ext } from "./Modal";

const services = [
  {
    icon: PackageOpen,
    title: "Gestión de extracciones",
    text: "Actuamos como extractores autorizados por cuenta de terceros en depósitos fiscales de alcohol, bebidas derivadas e hidrocarburos. Tramitación ágil ante la AEAT con total trazabilidad fiscal.",
    modal: (
      <>
        <p>FiscalBond actúa como extractor autorizado <strong>(RETEF Nº 004VBSG6OC001684166)</strong> por cuenta de empresas sin registro propio.</p>
        <h4>¿Qué incluye?</h4>
        <ul>
          <li>Emisión de la orden de extracción al depósito fiscal</li>
          <li>Coordinación con el depósito para la salida física del producto</li>
          <li>Gestión del documento e-DA o DAA según destino</li>
          <li>Tramitación ante la AEAT en el sistema EMCS</li>
          <li>Liquidación del impuesto en el momento y lugar correctos</li>
          <li>Registro en el Libro de Impuestos Especiales</li>
        </ul>
        <h4>Modalidades</h4>
        <ul>
          <li>Extracción para puesta a consumo en España</li>
          <li>Extracción para reexpedición intracomunitaria (sin liquidar IIEE en España)</li>
          <li>Extracción para exportación a terceros países</li>
        </ul>
        <h4>Normativa</h4>
        <ul>
          <li><Ext href="https://www.boe.es/diario_boe/txt.php?id=BOE-A-2023-8993">RD 249/2023</Ext></li>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1992-26834">Ley 38/1992, art. 4</Ext></li>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1995-17640">RD 1165/1995, arts. 11 y ss.</Ext></li>
        </ul>
      </>
    ),
  },
  {
    icon: FileText,
    title: "Gestión documental y fiscal",
    text: "Declaraciones de impuestos especiales, modelos fiscales, contabilidad de movimientos, liquidaciones de IIEE nacionales y reexpedición a la UE.",
    modal: (
      <>
        <h4>Documentos que gestionamos</h4>
        <ul>
          <li>e-AD (Documento Administrativo Electrónico) en sistema EMCS</li>
          <li>DAA para exportaciones</li>
          <li>Libro Registro de Impuestos Especiales</li>
          <li>Modelos fiscales: 561 (Alcohol), 566 (Productos Intermedios), 570 y 580 (Hidrocarburos)</li>
          <li>Comunicaciones con la Oficina Gestora de Impuestos Especiales</li>
        </ul>
        <h4>Sistema EMCS</h4>
        <p>Mensajes que gestionamos: IE815, IE818, IE819, IE825, IE829.</p>
        <p><Ext href="https://www.agenciatributaria.es/AEAT.internet/Inicio/La_Agencia_Tributaria/Aduanas_e_Impuestos_Especiales/Impuestos_Especiales/EMCS.shtml">Más información EMCS — AEAT</Ext></p>
        <h4>Normativa</h4>
        <ul>
          <li>Orden HFP/381/2023</li>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1995-17640">RD 1165/1995</Ext></li>
        </ul>
      </>
    ),
  },
  {
    icon: ArrowLeftRight,
    title: "Intermediación y trading",
    text: "Conectamos compradores y vendedores en régimen suspensivo. Compraventas dentro del depósito fiscal con total seguridad jurídica y sin devengo del impuesto.",
    modal: (
      <>
        <p>Una compraventa en régimen suspensivo es una operación donde el producto cambia de propietario sin salir del depósito fiscal. <strong>El impuesto no se devenga.</strong></p>
        <h4>Operaciones que gestionamos</h4>
        <ul>
          <li>Compraventa intracomunitaria en suspensión</li>
          <li>Operaciones triangulares (proveedor UE → depósito España → cliente UE)</li>
          <li>Reexpedición desde depósito fiscal español a depósito en otro Estado Miembro</li>
          <li>Trading de alcohol a granel (etílico, vínico, agrícola)</li>
          <li>Trading de bebidas espirituosas en régimen suspensivo</li>
        </ul>
        <h4>Normativa</h4>
        <ul>
          <li><Ext href="https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX%3A32020L0262">Directiva 2020/262/UE</Ext></li>
          <li><Ext href="https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX%3A32013R0952">Reglamento UE 952/2013</Ext></li>
        </ul>
      </>
    ),
  },
  {
    icon: Scale,
    title: "Representación fiscal",
    text: "Actuamos como representantes fiscales de empresas nacionales o extranjeras que operan con productos sujetos a IIEE, asumiendo la interlocución con la AEAT.",
    modal: (
      <>
        <h4>¿Cuándo se necesita representante fiscal?</h4>
        <ul>
          <li>Empresas no establecidas en España con operaciones sujetas a IIEE</li>
          <li>Importadores de terceros países que introducen productos en depósito fiscal</li>
          <li>Empresas de otros EM que realizan adquisiciones intracomunitarias con destino España</li>
          <li>Operadores que necesitan CAE sin asumir la gestión directa</li>
        </ul>
        <h4>¿Qué incluye?</h4>
        <ul>
          <li>Obtención y mantenimiento del CAE</li>
          <li>Constitución y gestión de garantías ante la AEAT</li>
          <li>Presentación de declaraciones periódicas</li>
          <li>Interlocución con la Oficina Gestora</li>
          <li>Respuesta a requerimientos de la AEAT</li>
          <li>Gestión de devoluciones de IIEE</li>
        </ul>
        <p><Ext href="https://www.agenciatributaria.es/AEAT.internet/Inicio/La_Agencia_Tributaria/Aduanas_e_Impuestos_Especiales/Impuestos_Especiales/Registro_territorial.shtml">Más información sobre el CAE — AEAT</Ext></p>
      </>
    ),
  },
  {
    icon: BookOpen,
    title: "Asesoría en impuestos especiales",
    text: "Impuesto sobre Hidrocarburos, Alcohol y Bebidas Derivadas, gestión de CAE, constitución de garantías y cumplimiento normativo integral.",
    modal: (
      <>
        <h4>Tipos impositivos vigentes de referencia</h4>
        <ul>
          <li>Alcohol etílico: 830,25 €/hl ap (Tarifa 1ª, grupo I)</li>
          <li>Bebidas espirituosas: 10,18 €/litro de alcohol puro</li>
          <li>Gasoil automoción: 307,00 €/1.000 litros</li>
          <li>Gasolina sin plomo 95: 400,69 €/1.000 litros</li>
        </ul>
        <h4>Áreas de asesoría</h4>
        <ul>
          <li>Tipos impositivos y exenciones (alcohol desnaturalizado, uso industrial)</li>
          <li>Requisitos para operar como depósito fiscal, almacén fiscal o destinatario registrado</li>
          <li>Gasóleo bonificado y beneficios fiscales en el sector agrícola</li>
          <li>IVA en operaciones con impuestos especiales</li>
          <li>IVA a la importación diferido (art. 167 Ley 37/1992)</li>
        </ul>
        <h4>Normativa</h4>
        <ul>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1992-26834">Ley 38/1992</Ext></li>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1992-28740">Ley 37/1992 (IVA)</Ext></li>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1998-23284">Ley 34/1998 (Sector Hidrocarburos)</Ext></li>
        </ul>
      </>
    ),
  },
  {
    icon: Truck,
    title: "Logística coordinada",
    text: "Transporte nacional e internacional, gestión de INCOTERMS (FOB, CIF, DAP) y operaciones triangulares extracomunitarias en coordinación con depósitos autorizados.",
    modal: (
      <>
        <h4>INCOTERMS 2020 que gestionamos habitualmente</h4>
        <ul>
          <li>EXW — el comprador recoge en origen; coordinamos documentación de salida</li>
          <li>FOB — exportaciones marítimas; gestión del despacho y DUA</li>
          <li>CIF — transporte + seguro hasta puerto destino; coordinación con transitario</li>
          <li>DAP — entrega en domicilio del comprador; gestión completa incluyendo despacho de importación en destino</li>
          <li>DDP — entrega con todos los impuestos pagados</li>
        </ul>
        <h4>Operaciones triangulares</h4>
        <p>Tres partes en al menos dos países: proveedor A → intermediario B → comprador C. FiscalBond gestiona la coordinación documental completa.</p>
        <h4>Transporte especializado</h4>
        <ul>
          <li>ADR clase 3 para alcohol (líquidos inflamables)</li>
          <li>Cisternas para hidrocarburos</li>
          <li>Temperatura controlada para productos vínicolas</li>
        </ul>
        <p><Ext href="https://iccspain.org/incoterms-2020/">Referencia INCOTERMS 2020</Ext></p>
      </>
    ),
  },
  {
    icon: Warehouse,
    title: "Soluciones con depósitos fiscales",
    text: "Para operaciones que requieren almacenamiento físico, colaboramos con depósitos fiscales autorizados: extracción + almacenamiento + gestión fiscal en una sola solución.",
    modal: (
      <>
        <p>Un depósito fiscal es un establecimiento autorizado por la AEAT donde se almacenan productos en régimen suspensivo. El impuesto no se devenga mientras el producto permanece en el depósito.</p>
        <h4>Depósitos colaboradores</h4>
        <ul>
          <li>BCN Bonded (Barcelona) — alcohol y bebidas derivadas, acceso a puerto de Barcelona</li>
          <li>Red de depósitos fiscales autorizados en Bilbao, Valencia, Madrid, Algeciras</li>
        </ul>
        <h4>Solución integral</h4>
        <ul>
          <li>Extracción autorizada (RETEF propio de FiscalBond)</li>
          <li>Almacenamiento en depósito fiscal colaborador</li>
          <li>Gestión fiscal y documental completa</li>
          <li>Logística de distribución nacional o reexpedición UE</li>
        </ul>
        <p><Ext href="https://www.agenciatributaria.es/AEAT.internet/Inicio/La_Agencia_Tributaria/Aduanas_e_Impuestos_Especiales/Impuestos_Especiales/Depositos_fiscales.shtml">Depósitos autorizados — AEAT</Ext></p>
      </>
    ),
  },
  {
    icon: Globe2,
    title: "Reexpedición UE y exportación",
    text: "Reexpedición a otros estados miembros en régimen suspensivo y exportación a terceros países sin liquidación de impuestos especiales, con toda la gestión aduanera.",
    modal: (
      <>
        <h4>Reexpedición intracomunitaria</h4>
        <p>Producto desde depósito fiscal español hacia otro Estado Miembro bajo régimen suspensivo mediante sistema EMCS y documento e-AD.</p>
        <ul>
          <li>NO se liquida el IIEE en España</li>
          <li>Se devenga en el Estado Miembro de destino</li>
          <li>FiscalBond gestiona todo el proceso EMCS: emisión e-AD, seguimiento, cierre con informe de recepción (IE818)</li>
        </ul>
        <h4>Exportación a terceros países</h4>
        <ul>
          <li>No se devenga el IIEE en ningún EM</li>
          <li>Gestionamos: DUA de exportación, prueba de salida del territorio aduanero UE, cierre del movimiento EMCS</li>
        </ul>
        <h4>Mercados habituales de exportación de nuestros clientes</h4>
        <p>México, Colombia, Perú, EE.UU., Japón, China, Suiza, Noruega, Reino Unido.</p>
        <p><Ext href="https://ec.europa.eu/taxation_customs/business/excise-duties-alcohol-tobacco-energy/excise-movement-control-system_en">Sistema EMCS UE</Ext></p>
      </>
    ),
  },
];

export const Services = () => {
  const [open, setOpen] = useState(null);
  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="bg-[#F5F7FA] py-24 md:py-32 text-[#1A1A2E]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold">
            Servicios
          </span>
          <h2
            className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold leading-tight"
            style={{ fontFamily: "Poppins" }}
          >
            Todo lo que un extractor RETEF puede hacer por tu empresa.
          </h2>
          <p className="mt-5 text-slate-700 text-lg">
            Ocho servicios diseñados para simplificar tu operativa fiscal y
            liberar la liquidez retenida por los impuestos especiales.
          </p>
        </motion.div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map(({ icon: Icon, title, text }, i) => (
            <motion.button
              type="button"
              key={title}
              onClick={() => setOpen(i)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
              data-testid={`service-card-${i}`}
              className="group relative text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0066FF] hover:-translate-y-1 hover:shadow-xl hover:shadow-[#0066FF]/10 cursor-pointer transition-all"
            >
              <div className="absolute top-6 right-6 text-xs font-bold text-slate-300 group-hover:text-[#FF6B00] transition-colors tabular-nums">
                0{i + 1}
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#0066FF]/10 flex items-center justify-center mb-5 group-hover:bg-[#0066FF] transition-colors">
                <Icon className="w-6 h-6 text-[#0066FF] group-hover:text-white transition-colors" strokeWidth={1.7} />
              </div>
              <h3 className="text-lg font-semibold text-[#0A1628] mb-2 leading-snug" style={{ fontFamily: "Poppins" }}>
                {title}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">{text}</p>
              <span className="mt-3 inline-block text-xs font-semibold text-[#0066FF]">Saber más →</span>
            </motion.button>
          ))}
        </div>
      </div>

      {open !== null && (
        <Modal open={open !== null} onClose={() => setOpen(null)} title={services[open].title} eyebrow={`Servicio 0${open + 1}`}>
          {services[open].modal}
        </Modal>
      )}
    </section>
  );
};

export default Services;
